import { addDoc, serverTimestamp, onSnapshot, query, orderBy, deleteDoc, doc, writeBatch } from 'firebase/firestore';
import { db } from './firebase.utils';
import { sanitizeForFirestore } from './serialization';
import { buildLotDescription, stripCardForLot } from '../whatnot/whatnotListingText';
import { isStaged, SALE_KIND, SALE_STATUSES } from '../sales/sales.constants';
import { transactionDoc, transactionsCollection } from './transactionPaths';

const DISCORD_PLATFORM = 'Discord';

const discordQueueFields = () => ({
  kind: SALE_KIND,
  platform: DISCORD_PLATFORM,
  status: SALE_STATUSES.STAGED,
});

export async function addCardToDiscord(uid, cardData, variation, context = {}) {
  const { sport, year, manufacturer, series, cardType, cardTypeCategory } = context;
  const docRef = await addDoc(transactionsCollection(uid), sanitizeForFirestore({
    cardNumber: cardData.cardNumber ?? null,
    name: cardData.name ?? [],
    team: cardData.team ?? [],
    cardAttributes: cardData.cardAttributes ?? [],
    cardParallelName: variation?.cardParallelName ?? null,
    cardColor: variation?.cardColor ?? null,
    cardPrintNumber: variation?.cardPrintNumber ?? null,
    cardSupply: variation?.cardSupply ?? null,
    sport: sport ?? null,
    year: year ?? null,
    manufacturer: manufacturer ?? null,
    series: series ?? null,
    cardType: cardType ?? null,
    cardTypeCategory: cardTypeCategory ?? null,
    type: 'single',
    ...discordQueueFields(),
    addedAt: serverTimestamp(),
  }));
  return docRef.id;
}

export function listenToDiscordCards(uid, callback) {
  const q = query(transactionsCollection(uid), orderBy('addedAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    const items = snapshot.docs
      .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      .filter((item) => item.platform === DISCORD_PLATFORM && isStaged(item.status));
    callback(items);
  }, () => callback([]));
}

export async function removeCardFromDiscord(uid, docId) {
  await deleteDoc(transactionDoc(uid, docId));
}

export async function createDiscordLot(uid, title, cardIds, cards) {
  const lotRef = doc(transactionsCollection(uid));
  const batch = writeBatch(db);
  batch.set(lotRef, sanitizeForFirestore({
    type: 'lot',
    title: title.trim(),
    description: buildLotDescription(cards),
    cards: cards.map(stripCardForLot),
    cardCount: cards.length,
    ...discordQueueFields(),
    addedAt: serverTimestamp(),
  }));
  cardIds.forEach((id) => batch.delete(transactionDoc(uid, id)));
  await batch.commit();
  return lotRef.id;
}

export async function ungroupDiscordLot(uid, lotItem) {
  const batch = writeBatch(db);
  (lotItem.cards ?? []).forEach((card) => {
    const singleRef = doc(transactionsCollection(uid));
    batch.set(singleRef, sanitizeForFirestore({
      ...stripCardForLot(card),
      type: 'single',
      ...discordQueueFields(),
      addedAt: serverTimestamp(),
    }));
  });
  batch.delete(transactionDoc(uid, lotItem.id));
  await batch.commit();
}
